import React, { useEffect, useRef, useState } from "react";
import {
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import axios from "../api/axios";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import ProductCard from "../components/ProductCard";
import Cart from "../components/Cart";
import CloseIcon from "@mui/icons-material/Close";
import { Add, Remove } from "@mui/icons-material";
import PrimaryButton from "../components/PrimaryButton";
import { useNavigate } from "react-router-dom";
import LogoutIcon from '@mui/icons-material/Logout';

const Products = () => {
    const navigate = useNavigate();
    const outlet = localStorage.getItem("outlet");
    const isEventOutlet = JSON.parse(localStorage.getItem("isEventOutlet"));
    const [productInfo, setProductInfo] = useState({});
    const [totalOfferPrice, setTotalOfferPrice] = useState(0);
    const [totalMrp, setTotalMrp] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedAddons, setSelectedAddons] = useState({});
    const [makingTime, setMakingTime] = useState("");
    const [cartItems, setCartItems] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState("ALL");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [clickedCategory, setClickedCategory] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [inputText, setInputText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [qtyChangingIds, setQtyChangingIds] = useState([]);
    const [isFetchData, setIsFetchData] = useState(false);
    const [isTabChange, setIsTabChange] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletingIds, setDeletingIds] = useState([]);

    const cartItemsRef = useRef(cartItems);
    useEffect(() => {
        cartItemsRef.current = cartItems;
    }, [cartItems]);

    const deleteCart = async (id) => {
        try {
            const token = localStorage.getItem("authToken");
            await axios.delete("cart/new", {
                headers: { Authorization: `Bearer ${token}` },
                data: { cartId: id },
            });
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                // Silently log — logout should always complete even if server delete fails
                console.error("deleteCart error:", errorMessage);
            }
        }
    };

    const clearCartLocally = () => {
        localStorage.removeItem("cart");
        localStorage.removeItem("cartItems");
        localStorage.removeItem("cartId");
        localStorage.removeItem("additionalCharges");
        localStorage.removeItem("cartReadyTime");
    };

    const handleLogout = async () => {
        const cartId = localStorage.getItem("cartId");
        const hasItems = cartItemsRef.current.length > 0;

        if (cartId && hasItems) {
            await deleteCart(cartId);
        }

        clearCartLocally();
        localStorage.removeItem("kioskUser");
        localStorage.removeItem("orderAmount");
        localStorage.removeItem("orderId");
        localStorage.removeItem("rzp_checkout_anon_id");
        localStorage.removeItem("rzp_device_id");

        navigate("/explore");
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        filtereByFoodTypeAndSearch(newValue, inputText, selectedCategory);
    };

    const handleCategoryClick = (categoryId) => {
        setClickedCategory((prevClickedCategory) => {
            if (prevClickedCategory === categoryId) {
                setSelectedCategory(null);
                filtereByFoodTypeAndSearch(selectedTab, inputText, null);
                return null;
            } else {
                const found = categoriesList.find((item) => item?._id === categoryId);
                if (found) {
                    setSelectedCategory(categoryId);
                    filtereByFoodTypeAndSearch(selectedTab, inputText, categoryId);
                    return categoryId;
                }
                return prevClickedCategory;
            }
        });
    };

    const toggleDrawer = (open) => () => {
        setIsDrawerOpen(open);
        setQuantity(1);
        setIsOpen(false);
    };

    const handleClickOpen = (productId) => {
        const product = productsList.find((item) => item._id === productId);
        if (!product) return;

        const initialAddons = {};
        let initialTotalPrice = product.newOfferPrice || 0;
        let initialTotalMrp = product.newMrp || 0;

        product.addons?.forEach((addon) => {
            if (!addon || addon.status !== "ACTIVE" || addon.isDelete) return;

            const validDefaultValues = addon.values?.filter((item) => {
                const isDefault = item.isDefault === true;
                const isActive = item.status === 'ACTIVE';
                const notExcluded =
                    !(item.excludedOutlets || []).includes(outlet) &&
                    !(item.excludedValues || []).includes(item._id);
                return isDefault && isActive && notExcluded;
            });

            if (validDefaultValues?.length > 0) {
                validDefaultValues.forEach((item) => {
                    if (addon.isMultipleSelect === true) {
                        if (!initialAddons[addon._id]) initialAddons[addon._id] = [];
                        initialAddons[addon._id].push({
                            _id: addon._id,
                            addonValueId: item._id,
                            selectedValue: item.value,
                            isDefault: item.isDefault,
                            price: item.price,
                            status: item.status,
                            excludedOutlets: item.excludedOutlets,
                            excludedValues: item.excludedValues,
                        });
                    } else {
                        initialAddons[addon._id] = {
                            _id: addon._id,
                            addonValueId: item._id,
                            selectedValue: item.value,
                            isDefault: item.isDefault,
                            price: item.price,
                            status: item.status,
                            excludedOutlets: item.excludedOutlets,
                            excludedValues: item.excludedValues,
                        };
                    }
                    initialTotalPrice += item.price || 0;
                    initialTotalMrp += item?.mrp != null ? item.mrp : 0;
                });
            }
        });

        setSelectedAddons(initialAddons);
        setTotalOfferPrice(initialTotalPrice);
        setTotalMrp(initialTotalMrp);
        setProductInfo(product);
        toggleDrawer(true)();
    };

    const getFoodType = (foodType) => {
        switch (foodType) {
            case "VEG": return "/assets/VegImagee.png";
            case "NON-VEG": return "/assets/NonVeg.png";
            case "VEGAN": return "/assets/Vegan.png";
            case "VEG/VEGAN": return ["/assets/VegImagee.png", "/assets/Vegan.png"];
            default: return "https://example.com/images/unknown.png";
        }
    };

    const handleAddonClick = (addonId, addonValueId, addonPrice = 0, addonName, addonMrp = 0, isMultipleSelect = false, isDefault = false, status, excludedOutlets, excludedValues) => {
        const numericAddonPrice = Number(addonPrice) || 0;
        const numericAddonMrp = Number(addonMrp) || 0;

        setSelectedAddons((prevSelectedAddons) => {
            const newSelectedAddons = { ...prevSelectedAddons };

            if (isMultipleSelect) {
                if (!Array.isArray(newSelectedAddons[addonId])) newSelectedAddons[addonId] = [];

                const existingIndex = newSelectedAddons[addonId].findIndex(
                    (item) => item.addonValueId === addonValueId
                );

                if (existingIndex > -1) {
                    if (!newSelectedAddons[addonId][existingIndex].isDefault) {
                        newSelectedAddons[addonId] = [...newSelectedAddons[addonId]];
                        newSelectedAddons[addonId].splice(existingIndex, 1);
                        setTotalOfferPrice((prev) => prev - numericAddonPrice);
                        setTotalMrp((prev) => prev - numericAddonMrp);
                    }
                } else {
                    newSelectedAddons[addonId] = [
                        ...newSelectedAddons[addonId],
                        { _id: addonId, addonValueId, price: numericAddonPrice, selectedValue: addonName, mrp: numericAddonMrp, isDefault, status, excludedOutlets, excludedValues },
                    ];
                    setTotalOfferPrice((prev) => prev + numericAddonPrice);
                    setTotalMrp((prev) => prev + numericAddonMrp);
                }
            } else {
                const previousAddon = newSelectedAddons[addonId];
                const previousPrice = previousAddon ? (Number(previousAddon.price) || 0) : 0;
                const previousMrp = previousAddon ? (Number(previousAddon.mrp) || 0) : 0;

                newSelectedAddons[addonId] = {
                    _id: addonId, addonValueId, price: numericAddonPrice, selectedValue: addonName,
                    mrp: numericAddonMrp, isDefault, status, excludedOutlets, excludedValues,
                };

                setTotalOfferPrice((prev) => prev - previousPrice + numericAddonPrice);
                setTotalMrp((prev) => prev - previousMrp + numericAddonMrp);
            }

            return newSelectedAddons;
        });
    };

    const preparePayloadWithAddons = (productData, selectedAddons = {}, newQuantity) => {
        const productId = productData._id;
        const qty = (Number(newQuantity) || 0) + 1;

        let addons = [];
        if (selectedAddons && Object.keys(selectedAddons).length > 0) {
            addons = Object.values(selectedAddons).flat().map((addon) => ({
                _id: addon._id,
                selectedValue: addon.selectedValue,
                addonValueId: addon.addonValueId,
                price: addon.price,
            }));
        }

        const cartItem = { product: productId, quantity: qty, sourceType: 'KIOSK' };
        if (addons.length > 0) cartItem.addons = addons;

        return { ordersType: "PICKUP", outlet, cartItems: [cartItem] };
    };

    const preparePayloadWithUpdatedQuantity = (productData, selectedAddons, updatedQuantity) => {
        const productId = productData._id;
        const qty = Number(updatedQuantity) || 0;

        let addons = [];
        if (selectedAddons && Object.keys(selectedAddons).length > 0) {
            addons = Object.values(selectedAddons).map((addon) => ({
                _id: addon._id,
                selectedValue: addon.selectedValue,
                addonValueId: addon.addonValueId,
                price: addon.price,
                mrp: addon.mrp,
            }));
        }

        const cartItem = { product: productId, quantity: qty, sourceType: 'KIOSK' };
        if (addons.length > 0) cartItem.addons = addons;

        return { ordersType: "PICKUP", outlet, cartItems: [cartItem] };
    };

    const handleAddProductToCartWithAddons = async (productData) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const product = productsList.find((item) => item._id === productData?._id);
            if (!product) return;

            const addonTitles = {};
            if (product?.addons?.length) {
                product.addons.forEach((addon) => { addonTitles[addon._id] = addon.title; });
            }

            const validatedAddons = { ...selectedAddons };

            for (const addonId in validatedAddons) {
                const selected = validatedAddons[addonId];
                if (!selected) {
                    toast.error(`Please select a value for all required addons.`, { position: 'top-center', autoClose: 3000, theme: 'colored' });
                    return;
                }

                if (Array.isArray(selected)) {
                    const hasInvalidDefault = selected.some(
                        (val) => val?.isDefault && (val.status === "INACTIVE" || val.excludedOutlets?.includes(outlet) || val.excludedValues?.includes(val._id))
                    );
                    if (hasInvalidDefault) {
                        const activeValues = selected.filter(
                            (val) => val.status === "ACTIVE" && !val.excludedOutlets?.includes(outlet) && !val.excludedValues?.includes(val._id)
                        );
                        if (activeValues.length > 0) {
                            validatedAddons[addonId] = activeValues;
                        } else {
                            toast.error(`Please select another available option for "${addonTitles[addonId] || 'Addon'}"`, { position: 'top-center', autoClose: 3000, theme: 'colored' });
                            return;
                        }
                    }
                } else if (typeof selected === "object") {
                    if (selected?.isDefault && (selected.status === "INACTIVE" || selected.excludedOutlets?.includes(outlet) || selected.excludedValues?.includes(selected._id))) {
                        toast.error(`Please select another available option for "${addonTitles[addonId] || 'Addon'}"`, { position: 'top-center', autoClose: 3000, theme: 'colored' });
                        return;
                    }
                }
            }

            let payload;
            if (isOpen) {
                payload = preparePayloadWithUpdatedQuantity(productInfo, validatedAddons, quantity);
            } else {
                setIsOpen(true);
                payload = preparePayloadWithAddons(product, validatedAddons, quantity - 1);
            }

            const response = await axios.post("cart/new", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.status === 200) {
                const successMessage = response?.data?.data?.message;
                const newCartId = response?.data?.data?.cart?._id;
                localStorage.setItem("cartId", newCartId);
                toast.success(successMessage, { position: "top-center", autoClose: 2000, theme: "colored", type: "info" });
                setIsDrawerOpen(false);
                getCartItems();
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, theme: "colored" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getCartItems = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`cart/userCart/new/${outlet}/PICKUP?source=KIOSK`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response?.data?.status === true) {
                const items = response?.data?.data?.cartItems?.filter((item) => item?.product?.isDelete === false) || [];
                const readyTime = items.reduce((acc, item) => {
                    const mt = Number(item?.product?.makingTime);
                    return acc + (isNaN(mt) ? 0 : mt);
                }, 0);
                setMakingTime(readyTime);
                setCartItems(items);

                // FIX: if the cart is now empty, remove cartId from localStorage
                // so logout won't attempt to delete an already-empty/gone cart
                if (items.length === 0) {
                    localStorage.removeItem("cartId");
                }
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, theme: "colored" });
            }
        }
    };

    const deleteCartItem = async (id) => {
        setIsDeleting(true);
        setDeletingIds((prevIds) => [...prevIds, id]);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.delete("cart/new", {
                headers: { Authorization: `Bearer ${token}` },
                data: { cartItemIds: [id] },
            });

            if (response?.status === 200) {
                setCartItems((prevItems) => {
                    const updatedItems = prevItems.filter((item) => item._id !== id);

                    // FIX: cart is now empty — remove cartId so logout won't call deleteCart
                    if (updatedItems.length === 0) {
                        localStorage.removeItem("cartId");
                    }

                    return updatedItems;
                });
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, theme: "colored" });
            }
        } finally {
            setIsDeleting(false);
            setDeletingIds((prevIds) => prevIds.filter((itemId) => itemId !== id));
        }
    };

    const handleQuantityChange = async (productId, newQuantity) => {
        const previousCartItems = [...cartItems];
        setQtyChangingIds((prevIds) => [...prevIds, productId]);

        if (newQuantity === 0) {
            setCartItems((prevItems) => {
                const updatedItems = prevItems.filter((item) => item._id !== productId);
                // FIX: optimistically clear cartId when the last item is removed
                if (updatedItems.length === 0) {
                    localStorage.removeItem("cartId");
                }
                return updatedItems;
            });
        } else {
            setCartItems((prevItems) =>
                prevItems.map((item) =>
                    item._id === productId ? { ...item, quantity: newQuantity } : item
                )
            );
        }

        try {
            if (newQuantity === 0) {
                await deleteCartItem(productId);
                return;
            }

            const token = localStorage.getItem("authToken");
            const response = await axios.put("cart/new", {
                cartItems: [{ _id: productId, quantity: newQuantity }],
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.status === 200) {
                getCartItems();
            }
        } catch (error) {
            // Rollback on any error
            setCartItems(previousCartItems);
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, theme: "colored" });
            }
        } finally {
            setQtyChangingIds((prevIds) => prevIds.filter((id) => id !== productId));
        }
    };

    const handleInputChange = (e) => {
        const searchText = e.target.value;
        setInputText(searchText);
        filtereByFoodTypeAndSearch(selectedTab, searchText, selectedCategory);
    };

    const handleChangeQuantity = (change) => {
        const updatedQuantity = quantity + change;
        if (updatedQuantity < 1) return;
        setQuantity(updatedQuantity);
    };

    const getCategoriesWithProducts = async () => {
        setIsFetchData(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`product/getProductsWithAddons?outletId=${outlet}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: inputText },
            });
            if (response?.status === 200) {
                const categories = response?.data?.data?.categories;
                const filteredCategories = categories?.filter((item) =>
                    isEventOutlet ? item?.isEventCategory === true : item?.isEventCategory === false
                );
                setCategoriesList(filteredCategories);

                let products = filteredCategories.map((cat) => cat.products).flat();
                let filteredProducts = products?.filter((item) =>
                    isEventOutlet ? item?.isEventProduct === true : item?.isEventProduct === false
                );
                filteredProducts = filteredProducts.filter(
                    (product) => !(product.excludedOutlets && Array.isArray(product.excludedOutlets) && product.excludedOutlets.includes(outlet))
                );
                setProductsList(filteredProducts);
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, theme: "colored" });
            }
        } finally {
            setIsFetchData(false);
        }
    };

    const filtereByFoodTypeAndSearch = async (foodType, text, categoryId) => {
        setIsTabChange(true);
        try {
            const token = localStorage.getItem("authToken");
            const params = new URLSearchParams();
            if (outlet) params.append("outletId", outlet);
            if (foodType && foodType !== "ALL") params.append("foodType", foodType);
            if (categoryId) params.append("categoryId", categoryId);

            const url = `product/getProductsWithAddons?${params.toString()}`;
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

            if (response?.status === 200) {
                const rawCategories = response?.data?.data?.categories;
                const categories = Array.isArray(rawCategories) ? rawCategories : [];
                const filteredCategories = categories.filter((item) =>
                    isEventOutlet ? item?.isEventCategory === true : item?.isEventCategory === false
                );

                let products = filteredCategories.map((cat) => cat?.products || []).flat().filter((item) =>
                    isEventOutlet ? item?.isEventProduct === true : item?.isEventProduct === false
                );

                if (text) {
                    const searchText = text.toLowerCase();
                    products = products.filter((item) => item?.name?.toLowerCase().includes(searchText));
                }

                setProductsList(products);
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                switch (error.response?.status) {
                    case 406: errorMessage = error.response?.data?.error?.message || error.response?.data?.error; break;
                    case 412: errorMessage = error.response?.data?.data?.message; break;
                    case 500: errorMessage = error.response?.data?.error; break;
                    default: errorMessage = "An unexpected error occurred.";
                }
                toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, theme: "colored" });
            }
        } finally {
            setIsTabChange(false);
        }
    };

    useEffect(() => {
        if (!productInfo || !productInfo.newOfferPrice) return;
        const basePrice = Number(productInfo.newOfferPrice) || 0;
        const addonsPrice = Object.values(selectedAddons).reduce((total, addon) => {
            if (!addon) return total;
            if (Array.isArray(addon)) return total + addon.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
            return total + (Number(addon.price) || 0);
        }, 0);
        setTotalOfferPrice(basePrice + addonsPrice);
    }, [selectedAddons, productInfo]);

    useEffect(() => {
        const cartId = localStorage.getItem("cartId");
        if (cartId) getCartItems();
        setSelectedTab("ALL");
        setSelectedCategory(null);
        setInputText("");
        getCategoriesWithProducts();
    }, []);

    return (
        <>
            <AppBar position="fixed" color="inherit" elevation={0}>
                <Toolbar sx={{ padding: '10px', background: '#dfeef5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src="/assets/brand-logo.png" alt="Brand Logo" style={{ height: 50, marginRight: 5 }} />
                    </Box>
                    <Box sx={{ flexGrow: 2, display: 'flex', justifyContent: 'center' }}>
                        <FormControl sx={{ width: "70%" }}>
                            <OutlinedInput
                                size="small"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <SearchIcon sx={{ color: '#00b0ff' }} />
                                    </InputAdornment>
                                }
                                onChange={handleInputChange}
                                placeholder="Search for the perfect brew"
                                sx={{ background: "white" }}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={{ flexGrow: 0 }}>
                        <Button
                            variant="contained"
                            onClick={handleLogout}
                            sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                padding: '8px 12px', backgroundColor: '#fff', color: '#000',
                                '&:hover': { backgroundColor: '#f0f0f0' },
                            }}
                        >
                            <LogoutIcon sx={{ mb: 0.5, color: '#000' }} />
                            Close
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box sx={{ background: '#dfeef5', scrollbarWidth: 'none', height: '100vh' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Box sx={{ width: '63%' }}>
                        <Box sx={{
                            height: "240px", position: "fixed", width: "63%", bgcolor: "white",
                            top: 87, zIndex: 1, overflowX: 'hidden', scrollbarWidth: 'none', borderTopRightRadius: '50px',
                        }}>
                            <Box sx={{
                                width: '93%', height: '120px', overflowX: 'auto', scrollbarWidth: 'none',
                                display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', padding: '18px', margin: 'auto'
                            }}>
                                {categoriesList?.map((item, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => handleCategoryClick(item._id)}
                                        sx={{
                                            marginRight: '16px', textAlign: 'center', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%',
                                        }}
                                    >
                                        <Box sx={{
                                            borderRadius: '50%', width: 71, height: 71, display: 'flex',
                                            justifyContent: 'center', alignItems: 'center', marginBottom: '8px',
                                            border: clickedCategory === item._id ? '2px solid #03a9f5' : '2px solid transparent',
                                        }}>
                                            <img src={item?.imageURL} alt="category"
                                                style={{ width: "100%", height: "100%", objectFit: 'cover', borderRadius: '50%' }} />
                                        </Box>
                                        <Typography variant="body2" sx={{
                                            color: '#000', whiteSpace: "nowrap", overflow: "hidden",
                                            textOverflow: "ellipsis", display: "block", textAlign: 'center', marginTop: '4px',
                                        }}>
                                            {item?.name.length > 12 ? `${item?.name.split(" ")[0]}...` : item?.name}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box display="flex" flexDirection="row" alignItems="center" gap={2} ml={1} sx={{ width: '80%' }}>
                                <Typography variant="body1" color="textSecondary">Filter By</Typography>
                                <Box sx={{ backgroundColor: '#e3f2fd', borderRadius: 4, padding: '5px', display: 'flex' }}>
                                    {['ALL', 'VEG', 'NON-VEG'].map((tab) => (
                                        <Button
                                            key={tab}
                                            onClick={(e) => handleTabChange(e, tab)}
                                            sx={{
                                                color: selectedTab === tab ? '#fff' : '#636363',
                                                backgroundColor: selectedTab === tab ? '#03a9f5' : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: selectedTab === tab ? '#039be5' : '#f1f1f1',
                                                    color: selectedTab === tab ? 'white' : '#03a9f5',
                                                },
                                                borderRadius: '20px', padding: '5px 15px',
                                                fontWeight: 'bold', textTransform: 'capitalize', marginRight: '10px',
                                            }}
                                        >
                                            {tab === 'NON-VEG' ? 'Non Veg' : tab.charAt(0) + tab.slice(1).toLowerCase()}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        {isFetchData || isTabChange ? (
                            <Box sx={{ width: '50%', margin: 'auto', marginTop: '650px', marginLeft: "300px", height: "calc(100vh - 340px)" }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Box sx={{
                                flex: "0 0 63%", bgcolor: "white", marginTop: "320px", overflowY: "auto",
                                scrollbarWidth: 'none', height: "calc(100vh - 350px)", borderRadius: 2, padding: "10px 0px"
                            }}>
                                <Grid container sx={{ minHeight: "calc(100vh - 240px)", overflowY: "auto", scrollbarWidth: 'none', width: "100%" }}>
                                    {productsList && productsList.length > 0 ? (
                                        productsList.map((product, index) => (
                                            <Grid key={product._id} item xs={12} sm={4} md={4}>
                                                <ProductCard
                                                    product={product}
                                                    handleClickOpen={handleClickOpen}
                                                    getFoodType={getFoodType}
                                                    quantity={quantity}
                                                    index={index}
                                                    cartItems={cartItems}
                                                    productsList={productsList}
                                                />
                                            </Grid>
                                        ))
                                    ) : (
                                        <Box sx={{ width: '50%', margin: 'auto', marginTop: '300px' }}>
                                            No Products Found
                                        </Box>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ flex: "0 0 33%", overflowY: "auto", background: '#dfeef5', marginRight: '2%' }}>
                        {cartItems && cartItems.length > 0 ? (
                            <Cart
                                cartItems={cartItems}
                                makingTime={makingTime}
                                handleQuantityChange={handleQuantityChange}
                                qtyChangingIds={qtyChangingIds}
                                isDeleting={isDeleting}
                                deletingIds={deletingIds}
                            />
                        ) : (
                            <Box sx={{
                                display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column",
                                backgroundColor: "#f9f9f9", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", overflow: "hidden",
                                width: "33%", position: "fixed", right: 0, top: 90, height: "100vh",
                                bgcolor: "white", borderTopLeftRadius: '40px', marginRight: '10px'
                            }}>
                                <img src="/assets/empty-cart.png" width={"60%"} style={{ marginBottom: "16px" }} alt="empty cart" />
                                <Typography variant="h6" sx={{ color: "#555" }}>Your cart is currently empty</Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Box>

            <Box sx={{ overflow: "hidden" }}>
                <Drawer
                    anchor="bottom"
                    open={isDrawerOpen}
                    onClose={toggleDrawer(false)}
                    transitionDuration={500}
                    PaperProps={{
                        sx: {
                            maxWidth: "500px", margin: "auto", borderTopLeftRadius: "16px",
                            borderTopRightRadius: "16px", padding: 3, boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
                            display: "flex", flexDirection: "column",
                        },
                    }}
                >
                    <Box>
                        <Stack direction="row" spacing={2} sx={{ marginBottom: 1 }}>
                            <Box sx={{ width: "100px", height: "100px", flexShrink: 0 }}>
                                <img src={productInfo?.displayImageUrl} alt="product-image"
                                    style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                            </Box>
                            <Box>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" width={"100%"}>
                                    <Box>
                                        <IconButton onClick={toggleDrawer(false)}
                                            sx={{ position: "absolute", right: 8, top: 12, color: "#222124" }}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                </Stack>
                                <Stack direction={'row'} alignItems={'center'} mt={1} gap={1}>
                                    <Box mb={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {Array.isArray(getFoodType(productInfo?.foodType)) ? (
                                            getFoodType(productInfo?.foodType).map((url, index) => (
                                                <img key={index} src={url} alt={`Food type image ${index}`} width={18} style={{ marginRight: 4 }} />
                                            ))
                                        ) : (
                                            <img src={getFoodType(productInfo?.foodType)} alt="Food type" width={18} />
                                        )}
                                    </Box>
                                    {(productInfo?.weight != null && productInfo?.weightUnit) && (
                                        <Box sx={{ marginTop: '-5px' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: "text.secondary" }}>
                                                {productInfo.weight} {productInfo.weightUnit}
                                            </Typography>
                                        </Box>
                                    )}
                                    {productInfo?.calories != null && (
                                        <Box sx={{ marginTop: '-5px' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: "text.secondary" }}>
                                                | {productInfo.calories} Cal
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                                <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1, fontSize: '17px' }}>
                                    {productInfo?.name}
                                </Typography>
                                <Typography mt={1} variant="body2" sx={{ fontWeight: 'bold', color: "text.secondary", fontSize: '12px' }}>
                                    {productInfo?.description}
                                </Typography>
                                <Stack direction={"row"} spacing={1} rowGap={1} flexWrap="wrap" mt={1}>
                                    {productInfo?.productsNature?.map((nature, index) => (
                                        <Chip key={index} label={nature} variant="contained"
                                            sx={{ background: "#dfedf4", color: '#466674', maxWidth: '400px' }} />
                                    ))}
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>

                    {productInfo?.addons && productInfo.addons.length > 0 && (
                        <Box sx={{
                            flex: 1, overflowY: "auto", background: '#f0f4f5', padding: '10px',
                            height: '400px', maxHeight: '400px', position: 'relative',
                            "&::-webkit-scrollbar": { width: "6px", height: "8px" },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: "#00b0ff", borderRadius: "50px" },
                            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#00b0ff" },
                            "&::-webkit-scrollbar-track": { backgroundColor: "#c6c8ca" },
                        }}>
                            <Box>
                                {productInfo?.addons
                                    ?.filter(addon =>
                                        addon && addon.status === 'ACTIVE' &&
                                        (!addon.excludedOutlets || !addon.excludedOutlets.includes(outlet)) &&
                                        Array.isArray(addon.values) &&
                                        addon.values.some(item =>
                                            item && item.status === 'ACTIVE' &&
                                            (!item.excludedOutlets || !item.excludedOutlets.includes(outlet)) &&
                                            (!item.excludedValues || !item.excludedValues.includes(item._id))
                                        )
                                    )
                                    .map((addon) => (
                                        <Box key={addon._id}
                                            sx={{ marginBottom: 1, background: 'white', padding: '10px', borderRadius: '30px' }}>
                                            <Stack direction="row" spacing={1} justifyContent="space-between">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '10px' }}>
                                                    {addon?.badgeImage && (
                                                        <img style={{ width: '20%', paddingRight: '10px' }} src={addon?.badgeImage} alt="badge" />
                                                    )}
                                                    <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: '14px', marginTop: '15px' }}>
                                                        {addon?.title}
                                                    </Typography>
                                                </Box>
                                                <Stack direction={addon?.title === "Size" ? 'row' : 'column'} spacing={2} flexWrap="wrap">
                                                    {addon.values
                                                        .filter(item => {
                                                            if (!item) return false;
                                                            if (item?.status === 'INACTIVE' && item?.isDefault === false) return false;
                                                            if (item?.status === 'ACTIVE' && item?.isDefault === false && item.excludedOutlets.includes(outlet)) return false;
                                                            return true;
                                                        })
                                                        .map((item) => {
                                                            const isSelected = Array?.isArray(selectedAddons[addon?._id])
                                                                ? selectedAddons[addon._id].some(selected => selected.addonValueId === item._id)
                                                                : selectedAddons[addon._id]?.addonValueId === item._id;

                                                            const isDisabled =
                                                                (item?.isDefault === true && item.status === 'ACTIVE' && item?.excludedOutlets.includes(outlet)) ||
                                                                (item?.isDefault === true && item.status === 'ACTIVE' && item?.excludedValues?.includes(item?._id)) ||
                                                                (item?.isDefault === true && item.status === 'INACTIVE');

                                                            const showAssetImageFallback = !item?.selectedImage;
                                                            let imgSrc = "";
                                                            let imgStyle = { width: '50px', height: '50px', cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1 };

                                                            if (showAssetImageFallback && addon.title.toLowerCase() === "size") {
                                                                if (item.value === "Medium") {
                                                                    imgSrc = `/assets/medium-${isSelected ? 'full' : 'empty'}.jpeg`;
                                                                    imgStyle = { ...imgStyle, width: '26px', height: '45px' };
                                                                } else if (item.value === "Large") {
                                                                    imgSrc = `/assets/large-${isSelected ? 'full' : 'empty'}.jpeg`;
                                                                    imgStyle = { ...imgStyle, width: '25px', height: '55px' };
                                                                } else if (item.value === "Small" || item.value === "Regular") {
                                                                    imgSrc = `/assets/small-${isSelected ? 'full' : 'empty'}.jpeg`;
                                                                    imgStyle = { ...imgStyle, width: '20px', height: '35px' };
                                                                }
                                                            }

                                                            const addonClickArgs = [
                                                                addon._id, item._id, item.price ?? 0, item.value,
                                                                item.mrp ?? 0, addon?.isMultipleSelect, item.isDefault,
                                                                item.status, item.excludedOutlets, item.excludedValues
                                                            ];

                                                            return (
                                                                <Stack key={item._id} direction="column" alignItems="center" justifyContent="flex-end">
                                                                    {item?.selectedImage && item.nonSelectedImage ? (
                                                                        <img
                                                                            src={isDisabled ? item.selectedImage : (isSelected ? item.selectedImage : item.nonSelectedImage)}
                                                                            alt={item.value}
                                                                            style={{ width: '50px', height: '50px', objectFit: 'contain', cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1 }}
                                                                            onClick={() => { if (!isDisabled) handleAddonClick(...addonClickArgs); }}
                                                                        />
                                                                    ) : (
                                                                        imgSrc && (
                                                                            <img src={imgSrc} alt={item.value} style={imgStyle}
                                                                                onClick={() => { if (!isDisabled) handleAddonClick(...addonClickArgs); }} />
                                                                        )
                                                                    )}
                                                                    <Button
                                                                        variant={isSelected ? "contained" : "outlined"}
                                                                        disabled={isDisabled}
                                                                        onClick={() => { if (!isDisabled) handleAddonClick(...addonClickArgs); }}
                                                                        sx={{
                                                                            textTransform: "none", margin: "3px", width: "auto",
                                                                            fontSize: "0.75rem", borderRadius: "20px", padding: "1px 16px",
                                                                            backgroundColor: isSelected ? "#00b0ff" : "transparent",
                                                                            color: isSelected ? "#fff" : "inherit",
                                                                            borderColor: isSelected ? "transparent" : "#00b0ff",
                                                                            opacity: isDisabled ? 0.5 : 1,
                                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                            "&:hover": {
                                                                                backgroundColor: isDisabled ? "transparent" : isSelected ? "#00b0ff" : "#bfbfbf",
                                                                                borderColor: isDisabled ? "transparent" : isSelected ? "transparent" : "#bfbfbf",
                                                                            },
                                                                        }}
                                                                    >
                                                                        {item.value}
                                                                        <span style={{ color: isSelected ? "green" : "inherit", marginLeft: '5px' }}>
                                                                            {item?.price ? `+₹${item.price}` : "+₹0"}
                                                                        </span>
                                                                    </Button>
                                                                </Stack>
                                                            );
                                                        })}
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    ))}
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ paddingTop: 2, borderTop: "1px solid #ddd" }}>
                        <Stack direction="row" alignItems="center" justifyContent={'space-between'} mb={2}>
                            <Box sx={{ width: '30%' }}>
                                <Stack direction={"row"} spacing={1}>
                                    <Remove onClick={() => handleChangeQuantity(-1)} className="cursor-p"
                                        style={{ fontSize: "2rem", color: "#fff", backgroundColor: "#00b0ff", borderRadius: "50px" }} />
                                    <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} sx={{ width: "30px", textAlign: "center" }}>
                                        <Typography sx={{ fontSize: 22 }}>{quantity}</Typography>
                                    </Stack>
                                    <Add onClick={() => handleChangeQuantity(1)} className="cursor-p"
                                        style={{ fontSize: "2rem", color: "#fff", backgroundColor: "#00b0ff", borderRadius: "50px" }} />
                                </Stack>
                            </Box>
                            <Box sx={{ width: '60%' }}>
                                <PrimaryButton
                                    title={`Add Item | ₹ ${(quantity * (Number(totalOfferPrice) || 0)).toFixed(2)}`}
                                    onClick={() => handleAddProductToCartWithAddons(productInfo)}
                                    disabled={isLoading}
                                    spinner={isLoading}
                                    disabledTitle={"Please Wait..."}
                                    style={{ borderRadius: "30px", fontSize: "18px", width: '100%', background: '#00b0ff' }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Drawer>
            </Box>
        </>
    );
};

export default Products;